import { rest } from "@auto/services";
import { getServiceUri } from "@auto/metadata";

import { LIMIT } from "./constants";

const search = async (entity, payload) => {
  return await rest.request(
    getServiceUri() + `${entity}/search`,
    "POST",
    payload
  );
};

const allQuery = () => ({
  columns: [],
  query: "",
  limit: LIMIT,
  offset: 0,
});

const eqQuery = (field, value) => ({
  columns: [],
  query: `${field}==${value}`,
  limit: LIMIT,
  offset: 0,
});

export const loadRoots = async () => {
  const [departments, constituencies, arrondissements] = await Promise.all([
    search("department", allQuery()),
    search("constituency", allQuery()),
    search("arrondissement", allQuery()),
  ]);

  return {
    departments: departments || [],
    constituencies: constituencies || [],
    arrondissements: arrondissements || [],
  };
};

export const loadArrondissements = async (departmentGid) => {
  return (await search("arrondissement", eqQuery("departmentGid", departmentGid))) || [];
};

export const loadCommunesByArrondissement = async (arrondissementGid) => {
  return (await search("commune", eqQuery("arrondissementGid", arrondissementGid))) || [];
};

export const loadCommunesByConstituency = async (constituencyGid) => {
  return (await search("commune", eqQuery("constituencyGid", constituencyGid))) || [];
};

export const loadSections = async (communeGid) => {
  return (await search("section", eqQuery("communeGid", communeGid))) || [];
};

export const loadVotingCenters = async (sectionGid) => {
  return (await search("voting-center", eqQuery("sectionGid", sectionGid))) || [];
};

export const enableVotingCenter = async (gid) => {
  return await rest.request(
    getServiceUri() + `voting-center-management/${gid}/enable`,
    "POST",
    {}
  );
};

export const disableVotingCenter = async (gid) => {
  return await rest.request(
    getServiceUri() + `voting-center-management/${gid}/disable`,
    "POST",
    {}
  );
};

export const votingCenterSummary = async (gid) => {
  return rest.request(
    getServiceUri() + `voting-center-management/${gid}/summary`,
    "GET"
  );
};

export const redirectVotingCenter = async (sourceGid, targetGid, kind) => {
  return rest.request(
    getServiceUri() + `voting-center-management/${sourceGid}/redirect`,
    "POST",
    { targetVotingCenterGid: targetGid, kind }
  );
};

export const createEntity = async (entity, payload) => {
  requireGeographyEntity(entity);

  return await rest.request(
    getServiceUri() + entity,
    "POST",
    payload
  );
};

const GEOGRAPHY_ENTITIES = new Set([
  "department",
  "arrondissement",
  "constituency",
  "commune",
  "section",
  "voting-center",
]);

const requireGeographyEntity = (entity) => {
  if (!GEOGRAPHY_ENTITIES.has(entity)) {
    throw new Error(`Unknown geography entity: ${entity}`);
  }
};

const normalizePutUpdatePayload = (entity, payload) => {
  if (!payload || typeof payload !== "object" || Array.isArray(payload)) {
    throw new Error(`${entity}: PUT update payload must be an object.`);
  }

  const { id, ...cleanPayload } = payload;

  if (
    cleanPayload.gid === undefined ||
    cleanPayload.gid === null ||
    cleanPayload.gid === ""
  ) {
    throw new Error(`${entity}: gid is required for PUT update.`);
  }

  return cleanPayload;
};

export const updateEntity = async (entity, payload) => {
  requireGeographyEntity(entity);

  return await rest.request(
    getServiceUri() + entity,
    "PUT",
    normalizePutUpdatePayload(entity, payload)
  );
};

// The generated search APIs are the registry's actual browsing contract.
// The management API exposes /targets (a capped active-target lookup), not /centers.
// Build the Rediriger browser from the unrestricted, paginated entity searches.
const REDIRECTION_INDEX_PAGE_SIZE = LIMIT;
let geographyIndexCache = null;

const searchPage = (entity, query, limit, offset, sort) => search(entity, {
  columns: [], query, limit, offset,
  ...(sort ? { sort } : {}),
});

const searchAll = async (entity) => {
  const rows = [];
  for (let offset = 0; ; offset += REDIRECTION_INDEX_PAGE_SIZE) {
    const page = await searchPage(entity, null, REDIRECTION_INDEX_PAGE_SIZE, offset, [
      { selector: "gid", direction: "asc" },
    ]);
    rows.push(...page);
    if (page.length < REDIRECTION_INDEX_PAGE_SIZE) return rows;
  }
};

const byGid = (rows) => new Map(rows.map((row) => [Number(row.gid), row]));

const makeGeographyIndex = async () => {
  const [departments, arrondissements, communes, sections] = await Promise.all([
    searchAll("department"), searchAll("arrondissement"),
    searchAll("commune"), searchAll("section"),
  ]);
  const departmentByGid = byGid(departments);
  const arrondissementByGid = byGid(arrondissements);
  const communeByGid = byGid(communes);
  const sectionByGid = new Map();
  const sectionGidsByDepartment = new Map();

  for (const section of sections) {
    const commune = communeByGid.get(Number(section.communeGid));
    const arrondissement = arrondissementByGid.get(Number(commune?.arrondissementGid));
    const department = departmentByGid.get(Number(arrondissement?.departmentGid));
    const departmentGid = department?.gid == null ? null : Number(department.gid);
    const entry = {
      sectionName: section.name || "",
      communeName: commune?.name || "",
      arrondissementName: arrondissement?.name || "",
      departmentName: department?.name || "",
      departmentGid,
    };
    sectionByGid.set(Number(section.gid), entry);
    if (departmentGid != null) {
      if (!sectionGidsByDepartment.has(departmentGid)) {
        sectionGidsByDepartment.set(departmentGid, []);
      }
      sectionGidsByDepartment.get(departmentGid).push(Number(section.gid));
    }
  }

  return { sectionByGid, sectionGidsByDepartment };
};

const getGeographyIndex = () => {
  const serviceUri = getServiceUri();
  if (!geographyIndexCache || geographyIndexCache.serviceUri !== serviceUri) {
    const promise = makeGeographyIndex();
    geographyIndexCache = { serviceUri, promise };
    promise.catch(() => {
      if (geographyIndexCache?.promise === promise) geographyIndexCache = null;
    });
  }
  return geographyIndexCache.promise;
};

const rsqlIn = (field, gids) => `${field}=in=(${gids.join(",")})`;
const escapeRsqlRegex = (value) => String(value)
  .replace(/\\/g, "\\\\")
  .replace(/"/g, '\\"')
  .replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const browserQuery = ({ searchText, departmentGid, gid }, geography) => {
  const conditions = [];

  if (gid) conditions.push(`gid==${gid}`);

  if (departmentGid) {
    const sectionGids = geography.sectionGidsByDepartment.get(departmentGid) || [];
    if (!sectionGids.length) return null;
    conditions.push(rsqlIn("sectionGid", sectionGids));
  }

  if (searchText) {
    const regex = `(?i).*${escapeRsqlRegex(searchText)}.*`;
    const matches = ["code", "name", "address"].map((field) => `${field}=re="${regex}"`);
    const text = searchText.toLocaleLowerCase();
    const sectionGids = [];
    for (const [sectionGid, place] of geography.sectionByGid) {
      if ([place.sectionName, place.communeName, place.arrondissementName, place.departmentName]
        .some((name) => name.toLocaleLowerCase().includes(text))) {
        sectionGids.push(sectionGid);
      }
    }
    if (sectionGids.length) matches.push(rsqlIn("sectionGid", sectionGids));
    if (/^\d+$/.test(searchText) && Number.isSafeInteger(Number(searchText))) {
      matches.push(`gid==${Number(searchText)}`);
    }
    conditions.push(`(${matches.join(",")})`);
  }

  return conditions.join(";") || null;
};

const redirectionEligibility = (center, role, excludeGid) => {
  const status = center.status == null ? null : Number(center.status);
  const canSource = status == null || status === 1 || status === 2;
  const canTarget = (status == null || status === 1) && center.sectionGid != null;
  let ineligibleReason = "";
  if (Number(center.gid) === excludeGid) ineligibleReason = "SAME_CENTER";
  else if (role === "SOURCE" && !canSource) ineligibleReason = "INVALID_STATUS";
  else if (role === "TARGET" && status != null && status !== 1) ineligibleReason = "NOT_ENABLED";
  else if (role === "TARGET" && center.sectionGid == null) ineligibleReason = "MISSING_SECTION";
  return {
    ...center,
    canSource,
    canTarget,
    eligible: !ineligibleReason,
    ineligibleReason,
  };
};

// Paginated search across the complete voting-center registry, with department and
// location names resolved from the existing electoral-geography entity endpoints.
// Final redirection checks (including existing source/destination relationships)
// remain authoritative in POST /voting-center-management/{gid}/redirect.
export const browseRedirectionCenters = async ({
  role, search: input = "", departmentGid = 0, gid = 0,
  excludeGid = 0, offset = 0, limit = 20,
}) => {
  if (role !== "SOURCE" && role !== "TARGET") {
    throw new Error(`Invalid voting-center selection role: ${role}`);
  }
  const geography = await getGeographyIndex();
  const query = browserQuery({
    searchText: String(input).trim(),
    departmentGid: Number(departmentGid),
    gid: Number(gid),
  }, geography);
  if (query === null && Number(departmentGid) !== 0) return { items: [], total: 0 };

  const [rows, total] = await Promise.all([
    searchPage("voting-center", query, limit, offset, [
      { selector: "code", direction: "asc" },
      { selector: "name", direction: "asc" },
      { selector: "gid", direction: "asc" },
    ]),
    rest.request(getServiceUri() + "voting-center/count", "POST", {
      columns: [], query, limit: null, offset: null,
    }),
  ]);
  return {
    total: Number(total),
    items: rows.map((center) => {
      const place = geography.sectionByGid.get(Number(center.sectionGid));
      return redirectionEligibility({
        ...center,
        sectionName: place?.sectionName || "",
        communeName: place?.communeName || "",
        departmentName: place?.departmentName || "",
      }, role, Number(excludeGid));
    }),
  };
};

export const loadDepartments = () => search("department", allQuery());
